import queryApi from 'api/query';
import { AxiosError, CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { clearCompanyDetailsCache, clearFlockCache } from '../../api-utils';
import { currentDropResponse, FlockListResponse, FlockResponse } from './flock-list-response';
import {
  BreedModel,
  FlockDetailModel,
  FlockEditModel,
  FlockGroupModel,
  FlockListingModel,
  FlockModel,
  FlockProfileDefaultTraitModel,
  FlockProfileGenomicProviderModel,
  FlockProfileOrderClaimRequest,
  FlockProfileOrderClaimResult,
  FlockProfileScenarioModel,
  FlockProfileTestModel,
  FlockProfileTestListModel,
  FlockUserIndexModel,
  MoveFlockRequest,
} from './models';

// Local type guard for AxiosError to support environments without the named export isAxiosError
const isAxiosError = (error: unknown): error is AxiosError => {
  return typeof error === 'object' && error !== null && (error as AxiosError).isAxiosError === true;
};

/**
 * Handles all requests to the `/flock` resource.
 */
class FlockApi {
  private resource = 'flock';

  /**
   * Gets logged in user's flocks.
   */
  getFlockByUser = async (token?: CancelToken) => {
    const response = await queryApi.get(`${this.resource}`, { cancelToken: token, cache: { maxAge: 0 } });
    return plainToClass(FlockListResponse, response.data as []);
  }

  /**
   * Gets flock profile tests available to the logged in user.
   */
  getFlockProfileTests = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/profiles`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileTestListModel, response.data as []);
  }

  /**
   * Gets a flock profile test by id.
   */
  getFlockProfileTest = async (id: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/profiles/${id}`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileTestModel, response.data);
  }

  /**
   * Gets flock profile tests available to an admin for a selected company.
   */
  getAdminCompanyFlockProfileTests = async (companyId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `flockprofile/admin/company/${companyId}/profiles`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileTestListModel, response.data as []);
  }

  /**
   * Gets a flock profile test by id for an admin selected company.
   */
  getAdminCompanyFlockProfileTest = async (companyId: number, id: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `flockprofile/admin/company/${companyId}/profiles/${id}`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileTestModel, response.data);
  }

  /**
   * Gets active genomic providers that can be used to claim Flock Profile orders.
   */
  getFlockProfileOrderClaimProviders = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/profile-order-claim/providers`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileGenomicProviderModel, response.data as []);
  }

  /**
   * Claims a Flock Profile order for a company using provider, order code/order ID, and order email.
   */
  claimFlockProfileOrder = async (
    companyId: number,
    request: FlockProfileOrderClaimRequest,
    token?: CancelToken,
  ) => {
    const response = await queryApi.post(
      `${this.resource}/company/${companyId}/profile-order-claim`,
      request,
      { cancelToken: token },
    );
    return plainToClass(FlockProfileOrderClaimResult, response.data);
  }

  /**
   * Gets scenario options used to determine recommended Flock Profile traits.
   */
  getFlockProfileScenarios = async (analysisId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `asbv/analysis-types/${analysisId}/scenarios`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileScenarioModel, response.data as []);
  }

  /**
   * Gets default traits for a Flock Profile scenario.
   */
  getFlockProfileScenarioDefaultTraits = async (scenarioId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `asbv/scenarios/${scenarioId}/default-traits`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockProfileDefaultTraitModel, response.data as []);
  }

  /**
   * Gets a flock by its code.
   */
  getFlockByFlockCode = async (flockCode: string, token?: CancelToken) => {
    const response = await queryApi.get(`${this.resource}/flock/${flockCode}`, { cancelToken: token, cache: { maxAge: 0 } });
    return plainToClass(FlockResponse, response.data);
  }

  /**
   * Gets the latest drop year for a flock.
   */
  getCurrentDropYear = async (flockCode: string, token?: CancelToken) => {
    const response = await queryApi.get(`${this.resource}/flock/latestDropYear/${flockCode}`, { cancelToken: token, cache: { maxAge: 0 } });
    return plainToClass(currentDropResponse, response.data);
  }

  /**
   * Gets extended flock information by code.
   */
  getFlockDetailByFlockCode = async (breedFlockCode: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/flock/${breedFlockCode}/detail`,
      { cancelToken: token, cache: { maxAge: 0 } },
    );
    return plainToClass(FlockDetailModel, response.data);
  }

  /**
   * Gets flock details required for editing.
   */
  getFlockForEdit = async (breedFlockCode: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/flock/${breedFlockCode}/edit`,
      {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          Expires: '0',
        },
        cancelToken: token, 
        cache: { 
          maxAge: 0 
        }
      },
    );
    return plainToClass(FlockEditModel, response.data);
  }

  /**
   * Creates a new flock for the current user.
   */
  createFlock = async (request: FlockEditModel, token?: CancelToken) => {
    const response = await queryApi.post(
      `${this.resource}`,
      request,
      { cancelToken: token },
    );
    const result = plainToClass(FlockModel, response.data);

    // Attempt to clear company cache after creating a flock.
    // CompanyId may be present on the request or in the response payload.
    const companyId = (request as any)?.companyId ?? (response.data as any)?.companyId;
    if (companyId) {
      try {
        await clearCompanyDetailsCache(companyId);
      } catch (e) {
        console.warn('Failed to clear company cache after createFlock:', e);
      }
    }

    return result;
  }

  /**
   * Updates an existing flock.
   */
  updateFlock = async (breedFlockCode: string, request: FlockEditModel, token?: CancelToken) => {
    try {
      await queryApi.put(
        `${this.resource}/${breedFlockCode}`,
        request,
        { cancelToken: token },
      );
      // Clear company cache if we can determine the companyId from the request
      const companyId = (request as any)?.companyId;
      
      if (companyId) {
        try {
          await clearCompanyDetailsCache(companyId);
          await clearFlockCache(breedFlockCode);
        } catch (e) {
          console.warn('Failed to clear company cache after updateFlock:', e);
        }
      }

      return true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Inactivates a flock.
   */
  inactivateFlock = async (breedFlockCode: string, token?: CancelToken) => {
    try {
      await queryApi.get(
        `${this.resource}/${breedFlockCode}/inactivate`,
        { cancelToken: token, cache: { maxAge: 0 } },
      );
      try {
        await clearFlockCache(breedFlockCode);
      } catch (e) {
        console.warn('Failed to clear flock cache after inactivateFlock:', e);
      }

      return true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Checks whether a flock exists.
   */
  checkFlockExists = async (breedFlockCode: string, token?: CancelToken) => {
    try {
      await queryApi.get(
        `${this.resource}/exists/${breedFlockCode}`,
        { cancelToken: token, cache: { maxAge: 0 } },
      );
      return true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Adds a flock to a company.
   */
  addFlockToCompany = async (
    breedFlockCode: string,
    companyId: number,
    options?: { token?: CancelToken; sourceCompanyId?: number | null },
  ) => {
    const { token, sourceCompanyId } = options ?? {};
    try {
      await queryApi.post(
        `${this.resource}/${breedFlockCode}/company/${companyId}`,
        undefined,
        { cancelToken: token },
      );
      // Clear cache for the company which the flock was added to
      try {
        await clearCompanyDetailsCache(companyId);
      } catch (e) {
        console.warn('Failed to clear company cache after addFlockToCompany:', e);
      }

      const previousCompanyId =
        typeof sourceCompanyId === 'number' && !Number.isNaN(sourceCompanyId)
          ? sourceCompanyId
          : null;
      if (previousCompanyId && previousCompanyId !== companyId) {
        try {
          await clearCompanyDetailsCache(previousCompanyId);
        } catch (e) {
          console.warn('Failed to clear previous company cache after addFlockToCompany:', e);
        }
      }

      return true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Moves a flock between companies.
   */
  moveFlock = async (request: MoveFlockRequest, token?: CancelToken) => {
    try {
      await queryApi.post(
        `${this.resource}/moveflock`,
        request,
        { cancelToken: token },
      );

      const uniqueCompanyIds = Array.from(new Set([request.sourceCompanyId, request.destinationCompanyId]));
      await Promise.all(uniqueCompanyIds.map(async (companyId) => {
        try {
          await clearCompanyDetailsCache(companyId);
        } catch (e) {
          console.warn(`Failed to clear company cache after moveFlock (companyId: ${companyId}):`, e);
        }
      }));

      return true;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Gets available breeds for a species.
   */
  getBreeds = async (species: string, token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/breeds`,
      {
        params: { species },
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(BreedModel, response.data as []);
  }

  /**
   * Gets all flock groups.
   */
  getFlockGroups = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/flock-groups`,
      {
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(FlockGroupModel, response.data as []);
  }

  /**
   * Gets all flock user indexes.
   */
  getFlockUserIndexes = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/flock-user-indexes`,
      {
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(FlockUserIndexModel, response.data as []);
  }

  /**
   * Gets flocks for the current user's company.
   */
  getFlocksForCurrentUsersCompany = async (token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/company/currentuser`,
      {
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(FlockListingModel, response.data as []);
  }

  /**
   * Gets flocks for a specific company.
   */
  getFlocksForCompany = async (companyId: number, token?: CancelToken) => {
    const response = await queryApi.get(
      `${this.resource}/company/${companyId}`,
      {
        cancelToken: token,
        cache: { maxAge: 0 },
      },
    );
    return plainToClass(FlockListingModel, response.data as []);
  }
}



export default new FlockApi();
